'use client';

import { useEffect, useRef, useCallback, useState, useImperativeHandle, forwardRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: TurnstileRenderOptions
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback?: (token: string) => void;
  'expired-callback'?: (() => void) | undefined;
  'error-callback'?: (() => void) | undefined;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
}

export interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact' | 'flexible';
  /** Controls when the widget is visible. Defaults to 'interaction-only' (invisible unless needed). */
  appearance?: 'always' | 'execute' | 'interaction-only';
  className?: string;
}

export interface TurnstileRef {
  reset: () => void;
}

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

/**
 * Cloudflare Turnstile widget for bot protection.
 * Renders in managed/invisible mode by default — most users never see it.
 *
 * Use the ref to imperatively reset the widget after form submissions
 * (tokens are single-use and expire after 5 minutes).
 */
export const TurnstileWidget = forwardRef<TurnstileRef, TurnstileWidgetProps>(
  function TurnstileWidget(
    {
      onVerify,
      onExpire,
      onError,
      theme = 'auto',
      size = 'flexible',
      appearance = 'interaction-only',
      className,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);

    const handleExpire = useCallback(() => {
      onExpire?.();
      // Auto-reset on expiry to get a fresh token
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }, [onExpire]);

    const renderWidget = useCallback(() => {
      if (!containerRef.current || !window.turnstile || !SITE_KEY) return;
      if (widgetIdRef.current) return;

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: onVerify,
        'expired-callback': handleExpire,
        'error-callback': onError,
        theme,
        size,
        appearance,
      });
    }, [onVerify, handleExpire, onError, theme, size, appearance]);

    // Expose reset method via ref so forms can reset after submission
    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }), []);

    useEffect(() => {
      if (!SITE_KEY) return;

      const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
      if (existingScript) {
        if (window.turnstile) {
          setScriptLoaded(true);
        } else {
          const prevOnload = window.onTurnstileLoad;
          window.onTurnstileLoad = () => {
            prevOnload?.();
            setScriptLoaded(true);
          };
        }
        return;
      }

      window.onTurnstileLoad = () => setScriptLoaded(true);

      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }, []);

    useEffect(() => {
      if (scriptLoaded) {
        renderWidget();
      }
    }, [scriptLoaded, renderWidget]);

    useEffect(() => {
      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, []);

    if (!SITE_KEY) return null;

    return <div ref={containerRef} className={className} />;
  }
);
